import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Compass, Plus, Pencil, Trash2, Link2, Loader2 } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SmartImage } from "@/components/SmartImage";

const CATEGORY_IDS = ["kadin", "erkek", "cocuk", "sevgili", "anne", "baba", "arkadas", "is_arkadasi", "yeni_ev", "dugun", "bebek", "mezuniyet"];

const empty = { name: "", price: "", store: "", image_url: "", product_url: "", categories: [], popularity: 50 };

export const AdminExploreProducts = () => {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [linkUrl, setLinkUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/explore-products");
      setProducts(res.data);
    } catch (err) {
      console.error("explore products load failed", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setLinkUrl(""); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, price: p.price ?? "", store: p.store || "", image_url: p.image_url || "",
      product_url: p.product_url || "", categories: p.categories || [], popularity: p.popularity ?? 50 });
    setLinkUrl("");
    setDialogOpen(true);
  };

  const fetchLink = async () => {
    if (!linkUrl.trim()) return;
    setFetching(true);
    try {
      const res = await api.post("/utils/fetch-link-metadata", { url: linkUrl.trim() });
      const d = res.data;
      setForm((f) => ({ ...f, name: d.name || f.name, image_url: d.image_url || f.image_url,
        price: d.price ?? f.price, store: d.store || f.store, product_url: d.product_url }));
      toast[d.success ? "success" : "info"](d.success ? t("item_fetched") : t("item_fetch_partial"));
    } catch {
      toast.error(t("link_read_fail"));
      setForm((f) => ({ ...f, product_url: linkUrl.trim() }));
    } finally { setFetching(false); }
  };

  const toggleCat = (c) => setForm((f) => ({
    ...f, categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("item_name_req")); return; }
    setBusy(true);
    const payload = { ...form, price: form.price === "" ? null : parseFloat(form.price), popularity: parseInt(form.popularity) || 0 };
    try {
      if (editing) await api.put(`/admin/explore-products/${editing.product_id}`, payload);
      else await api.post("/admin/explore-products", payload);
      toast.success(t("exp_saved"));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(t("del_item_confirm"))) return;
    try {
      await api.delete(`/admin/explore-products/${p.product_id}`);
      toast.success(t("item_deleted"));
      load();
    } catch { toast.error(t("error_generic")); }
  };

  return (
    <div className="bg-card rounded-2xl border border-border/50 p-6 mb-8" data-testid="admin-explore-section">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h2 className="font-heading font-semibold flex items-center gap-2">
          <Compass size={17} className="text-primary" /> {t("exp_mgmt")}
          <Badge variant="secondary" className="rounded-full">{products.length}</Badge>
        </h2>
        <Button size="sm" className="rounded-full" onClick={openNew} data-testid="admin-explore-add-btn">
          <Plus size={14} className="mr-1" /> {t("add_item")}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">{t("exp_hint")}</p>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
        {products.map((p) => (
          <div key={p.product_id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-secondary/50 transition-colors" data-testid={`admin-explore-row-${p.product_id}`}>
            <SmartImage src={p.image_url} alt={p.name} className="w-12 h-12 rounded-lg object-cover shrink-0"
              fallback={<div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0">🎁</div>} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {p.store} · {formatPrice(p.price)} · {(p.categories || []).map((c) => t(`cat_${c}`)).join(", ")}
              </p>
            </div>
            <Badge variant="outline" className="rounded-full shrink-0 text-xs" title={t("lbl_popularity")}>★ {p.popularity}</Badge>
            <div className="flex gap-1.5 shrink-0">
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8" onClick={() => openEdit(p)} aria-label={t("aria_edit")} data-testid={`admin-explore-edit-${p.product_id}`}>
                <Pencil size={12} />
              </Button>
              <Button size="icon" variant="outline" className="rounded-full w-8 h-8 text-destructive" onClick={() => remove(p)} aria-label={t("aria_delete")} data-testid={`admin-explore-delete-${p.product_id}`}>
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? t("edit_item") : t("add_item")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div className="flex gap-2">
                <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://www.trendyol.com/..."
                  className="rounded-xl" data-testid="admin-explore-link-input" />
                <Button onClick={fetchLink} disabled={fetching} className="rounded-full shrink-0" data-testid="admin-explore-fetch-btn">
                  {fetching ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />}
                </Button>
              </div>
            )}
            <SmartImage src={form.image_url} alt="" className="w-full h-36 object-cover rounded-xl" fallback={null} />
            <div>
              <Label>{t("lbl_item_name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-explore-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("lbl_price")}</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-explore-price-input" />
              </div>
              <div>
                <Label>{t("lbl_store")}</Label>
                <Input value={form.store} onChange={(e) => setForm({ ...form, store: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-explore-store-input" />
              </div>
            </div>
            <div>
              <Label>{t("lbl_product_url")}</Label>
              <Input value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <Label>{t("or_img_url")}</Label>
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-explore-image-input" />
            </div>
            <div>
              <Label>{t("lbl_categories")}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {CATEGORY_IDS.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`admin-explore-cat-${c}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      form.categories.includes(c) ? "bg-primary text-white border-primary" : "border-border text-muted-foreground"}`}>
                    {t(`cat_${c}`)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t("lbl_popularity")}</Label>
              <Input type="number" min="0" max="100" value={form.popularity}
                onChange={(e) => setForm({ ...form, popularity: e.target.value })} className="mt-1 rounded-xl w-32" data-testid="admin-explore-popularity-input" />
            </div>
            <Button onClick={save} disabled={busy} className="w-full rounded-full h-11 font-semibold" data-testid="admin-explore-save-btn">
              {busy ? t("saving") : t("save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
